import { debounce } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppRoutes from "@/constants/routes/AppRoutes";
import { ColumnFiltersState, SortingState } from "@tanstack/react-table";
import { useConversationTableStore } from "@/stores/conversation-table-state.store";
import { User } from "@/models/users/User";
import useUsers from "@/hooks/useUsers";
import useConversation from "@/hooks/useConversation";

const useConversationTableData = () => {
  const {
    data,
    search,
    pageCount,
    pageIndex,
    pageSize,
    sorting,
    columnFilters,
    setTableState,
  } = useConversationTableStore();

  const [searchValue, setSearchValue] = useState(search);
  const [users, setUsers] = useState<User[]>([]);
  const { useQueryAllConversations } = useConversation();

  const navigate = useNavigate();
  const { useQueryAllTenantUsers } = useUsers();

  const allUsers = useQueryAllTenantUsers();

  useEffect(() => {
    if (allUsers.isFetched && allUsers.data) {
      setUsers(allUsers.data);
    }
  }, [allUsers.data, allUsers.isFetched]);

  const allConversations = useQueryAllConversations({
    pageNumber: pageIndex + 1,
    pageSize,
    sortAscending: sorting.length ? !sorting[0].desc : false,
    sortBy: sorting.length ? sorting[0].id : "createdAt",
    search: search,
    filters: columnFilters,
  });

  // Effect to fetch data when dependencies change
  useEffect(() => {
    if (allConversations.isFetched && allConversations.data) {
      setTableState({
        data: allConversations.data.data,
        pageCount: allConversations.data.totalPages,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allConversations.isFetched, allConversations.data]);

  const handleRowClick = (rowIndex: number) => {
    const id = data[rowIndex].id;
    navigate(
      AppRoutes.insightsUserOverview(id, data[rowIndex].isDeleted?.toString()),
      {
        state: { conversationTitle: data[rowIndex].title },
      }
    );
  };

  // Debounced function to update the store state
  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setTableState({
          pageIndex: value !== search ? 0 : pageIndex,
          search: value,
        });
      }, 300),
    [setTableState, search, pageIndex]
  );

  // Synchronize searchValue with the store
  useEffect(() => {
    debouncedSetSearch(searchValue);
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [searchValue, debouncedSetSearch]);

  return {
    data: useMemo(() => data, [data]),
    searchValue,
    pageCount,
    pageIndex,
    pageSize,
    sorting,
    columnFilters,
    setSearchValue,
    setPageIndex: (pageIndex: number) => setTableState({ pageIndex }),
    setPageSize: (pageSize: number) => setTableState({ pageSize }),
    setSorting: (sorting: SortingState) => setTableState({ sorting }),
    setColumnFilters: (filters: ColumnFiltersState) =>
      setTableState({ columnFilters: filters }),
    handleRowClick,
    isLoading: allConversations.isPending,
    users,
  };
};

export default useConversationTableData;
